import { SafePaddingContainer } from '@/components/SafePaddingContainer';
import { SecondaryHeader } from '@/components/SecondaryHeader';
import { QuickBuySection } from '@/components/QuickBuySection';
import { RecurringSection } from '@/components/RecurringSection';
import { Button } from '@/components/ui/button';
import { openUrl } from '@/bridge/openUrl';
import { useCountdown } from '@/hooks/useCountdown';
import { useState, useEffect } from "react";
import { useFirebaseFunction } from "@/hooks/useFirebaseFunction";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";

// Define types for our API calls
type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    subscriptionsEnabled?: boolean;
    subscribedDollars?: number;
    entries?: number;
    // other fields that might be returned
  };
};

type UpdateUserPrivateInfoRequest = {
  updates: Partial<{
    subscriptionsEnabled?: boolean;
    subscribedDollars?: number;
    entries?: number;
    // other fields from UserPrivateInfo
  }>;
};

type UpdateUserPrivateInfoResponse = {
  success: boolean;
};

export function JackpotPage() {
    const { days, hours, minutes, seconds} = useCountdown();
    const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
    const [initialPrice, setInitialPrice] = useState<number | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [entries, setEntries] = useState<number>(0);

    const [userData, isLoading, fetchError] = useFirebaseFunction<
        GetUserPrivateInfoRequest,
        GetUserPrivateInfoResponse
    >(
        'getUserPrivateInfo',
        (fn) => fn({ fields: ["subscriptionsEnabled", "subscribedDollars", "entries"] }),
        []
    );

    // Set initial values when data is loaded and calculate entries
    useEffect(() => {
        if (userData && !isLoading) {
            const fetchedDollars = userData.userInfo.subscribedDollars ?? 0;
            const subscriptionsEnabled = userData.userInfo.subscriptionsEnabled ?? false;
            
            setSelectedPrice(fetchedDollars);
            setInitialPrice(fetchedDollars);
            
            // Calculate entries based on subscriptionsEnabled and subscribedDollars
            const calculatedEntries = subscriptionsEnabled && fetchedDollars > 0 ? 
                Math.floor(fetchedDollars) : 0;
            setEntries(calculatedEntries);

            console.log('[fetchedDollars]', userData);
        }
    }, [userData, isLoading]);
    
    // Show error toast if fetch fails
    useEffect(() => {
        if (fetchError) {
            // toast({
            //     title: "Error",
            //     description: "Failed to load subscription data. Please try again.",
            //     variant: "destructive"
            // });
        }
    }, [fetchError]);

    const handleUpdateSubscription = async (newPrice: number) => {
        // Set updating state
        setIsUpdating(true);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
                UpdateUserPrivateInfoRequest,
                UpdateUserPrivateInfoResponse
            >(
                "updateUserPrivateInfo",
                { 
                    updates: { 
                        subscribedDollars: newPrice,
                        // If this is the first time adding dollars, also enable subscriptions
                        ...(initialPrice === 0 ? { subscriptionsEnabled: true } : {})
                    } 
                }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialPrice(newPrice);
                setSelectedPrice(newPrice);
                
                // Recalculate entries
                const subscriptionsEnabled = userData?.userInfo.subscriptionsEnabled ?? 
                    (initialPrice === 0);
                const calculatedEntries = subscriptionsEnabled && newPrice > 0 ? 
                    newPrice : 0;
                setEntries(calculatedEntries);
                
                // toast({
                //     title: "Success",
                //     description: `Subscription updated to $${newPrice} weekly.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setSelectedPrice(initialPrice);
            console.error("Error updating subscription amount:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to update subscription amount.",
            //     variant: "destructive"
            // });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="flex flex-col h-screen overflow-y-scroll pb-10 bg-white">
            <SafePaddingContainer orientation='header' className='bg-blue'>
                <SecondaryHeader text={'Power Win'} />
                {/* Main content */}
            </SafePaddingContainer>
            <SafePaddingContainer orientation='footer' className='bg-white'>
                <div className="py-4 mx-4 max-w-md mx-2 mt-6 text-blue-dark border-b border-gray-300">
                    <div className='space-y-2 mb-4'>
                        <p className='text-lg'>200 prizes. Win up to:</p>
                        <h1 className='text-4xl font-semibold pb-1'>$4,600,000</h1>
                        <div className='h-8 font-bold text-white bg-pink-300 rounded-xl flex items-center px-4 w-5/6 tabular-nums'>
                            {`${days} days, ${hours} hrs, ${minutes} min, ${seconds} sec`}    
                        </div>
                    </div>


                    {
                        userData?.userInfo.subscriptionsEnabled ? (
                            <>
                                <p className='text-lg flex items-center mb-4'>
                                    My recurring entries: 
                                    <span className='bg-blue rounded-md text-white px-2 py-0.5 font-bold ml-2'>
                                        {entries}
                                    </span>
                                </p>

                                <p className='text-lg flex items-center mb-4'>
                                    My total entries: 
                                    <span className='bg-blue rounded-md text-white px-2 py-0.5 font-bold ml-2'>
                                        {userData?.userInfo?.entries ?? 0}
                                    </span>
                                </p>
                            </>
                        ) : (
                            <p>
                                Enable subscriptions in settings
                            </p>
                        )
                    }
                    
                    <p className='text-gray-400 text-xs mb-4'>
                        Prizes will rollover weekly until the qualifying number of entries is reached.
                        Please read the full Power Win terms, no purchase necessary
                    </p>
                </div>

                <div className="pb-4 mx-4 max-w-md mx-2 mt-6 text-blue-dark border-b border-gray-300">
                    <div className='mb-6'>
                        <QuickBuySection />
                    </div>
                    <RecurringSection 
                        selectedPrice={selectedPrice} 
                        initialPrice={initialPrice} 
                        isLoading={isLoading}
                        isUpdating={isUpdating}
                        fetchError={fetchError ?? null}
                        onUpdateSubscription={(price: number) => handleUpdateSubscription(price)}
                    />
                </div>

                <div className="pb-4 mx-4 max-w-md mx-2 mt-6 text-blue-dark space-y-3">
                    <p>
                        More info and options
                    </p>
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                    <div className='flex items-center px-2 border border-gray-300 py-2 font-medium justify-between'
                        onClick={() => openUrl('https://skillscratch.com/legal/sweeps')}>
                        Read official terms
                        <Button className='font-white font-semibold bg-blue-dark' size='sm' variant='wallet-btn'>Read</Button>
                    </div>
                    <p className='text-gray-400 text-xs mb-4'>
                        No purchase necessary. You may enter for free through a free write-in entry option.
                        Instructions available at the bottom of the official terms.
                    </p>
                </div>
            </SafePaddingContainer>
        </div>
    );
}